/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description:
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-08-26 15:39:30
 */
import { useState, useCallback } from 'react'
import { useSensorTracking } from './useSensorTracking'
import type { RecognitionRecord } from '../types/contextInterface'

/**
 * 反馈Modal数据类型
 */
export interface FeedbackData {
  /** 记录数据 */
  record: RecognitionRecord
  /** 字段名称 */
  fieldName: string
}

/**
 * 反馈提交参数类型
 */
export interface FeedbackSubmitParams {
  /** 评分 (0-10) */
  score: number
  /** 反馈内容 */
  content: string
  /** 是否愿意电话回访 */
  willPhoneCallback?: boolean
}

/**
 * 反馈Modal管理Hook
 * 统一管理反馈Modal的显示状态和数据
 */
export const useFeedbackModal = () => {
  // Modal可见性状态
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false)

  // Modal数据状态
  const [feedbackData, setFeedbackData] = useState<FeedbackData | null>(null)

  // 获取埋点上报方法
  const {
    reportAISecurityPageActionClick,
    reportAISecurityFeedback
  } = useSensorTracking()

  /**
   * 显示反馈Modal
   * @param data 反馈数据 (可选)
   */
  const showFeedbackModal = useCallback((data?: FeedbackData) => {
    if (data) {
      setFeedbackData(data)
    }
    setFeedbackModalVisible(true)
  }, [])

  /**
   * 隐藏反馈Modal
   */
  const hideFeedbackModal = useCallback(() => {
    setFeedbackModalVisible(false)
    setFeedbackData(null)
  }, [])

  /**
   * 处理正面反馈点击 (准确)
   * @param data 反馈数据 (可选)
   */
  const handlePositiveFeedback = useCallback(
    (data?: FeedbackData) => {
      reportAISecurityPageActionClick({
        actionType: 'feedbackPositive',
        bool1: true,
        ...(data?.fieldName && { string1: data.fieldName })
      })
      showFeedbackModal(data)
    },
    [reportAISecurityPageActionClick, showFeedbackModal]
  )

  /**
   * 处理负面反馈点击 (不准确)
   * @param data 反馈数据 (可选)
   */
  const handleNegativeFeedback = useCallback(
    (data?: FeedbackData) => {
      reportAISecurityPageActionClick({
        actionType: 'feedback',
        bool1: false,
        ...(data?.fieldName && { string1: data.fieldName })
      })
      showFeedbackModal(data)
    },
    [reportAISecurityPageActionClick, showFeedbackModal]
  )

  /**
   * 处理反馈提交
   * @param params 反馈参数
   */
  const handleFeedbackSubmit = useCallback(
    (params: FeedbackSubmitParams) => {
      // 上报满意度反馈埋点
      reportAISecurityFeedback({
        score: params.score,
        content: params.content,
        willPhoneCallback: params.willPhoneCallback,
        // 添加反馈相关的上下文信息
        ...(feedbackData && {
          record: feedbackData.record,
          fieldName: feedbackData.fieldName
        })
      })

      // 隐藏Modal并清理数据
      hideFeedbackModal()
    },
    [reportAISecurityFeedback, feedbackData, hideFeedbackModal]
  )

  /**
   * 处理反馈取消
   */
  const handleFeedbackCancel = useCallback(() => {
    hideFeedbackModal()
  }, [hideFeedbackModal])

  return {
    // 状态
    feedbackModalVisible,
    feedbackData,

    // 操作方法
    showFeedbackModal,
    hideFeedbackModal,
    handlePositiveFeedback,
    handleNegativeFeedback,
    handleFeedbackSubmit,
    handleFeedbackCancel
  }
}

export default useFeedbackModal
